import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../../../lib/api-client';

import type {
  IntegrationMetricsWire,
  ReconciliationFlagWire,
  ReconciliationRunResultWire,
  ResolveReconciliationFlagInput,
} from '../types';

/**
 * A4 REVAMP — hooks de reconciliation (Brief 5 Fatia 3a · #314).
 *
 * - `useIntegrationMetrics`: KPIs (última reconciliation + mismatches pendentes).
 * - `useReconciliationFlags`: divergências do MismatchDrawer.
 * - `useRunReconciliation`: força reconciliation on-demand (invalida métricas+flags).
 * - `useResolveReconciliationFlag`: resolve uma divergência (idem invalidação).
 *
 * Cache keys canonical: `['admin', 'integrations', 'reconciliation', ...]`.
 */

export const RECONCILIATION_METRICS_KEY = [
  'admin',
  'integrations',
  'reconciliation',
  'metrics',
] as const;

export const RECONCILIATION_FLAGS_KEY = [
  'admin',
  'integrations',
  'reconciliation',
  'flags',
] as const;

export function useIntegrationMetrics(): ReturnType<typeof useQuery<IntegrationMetricsWire>> {
  return useQuery({
    queryKey: RECONCILIATION_METRICS_KEY,
    queryFn: () => api.getIntegrationMetrics(),
    staleTime: 30_000,
  });
}

export function useReconciliationFlags(
  enabled = true,
): ReturnType<typeof useQuery<ReconciliationFlagWire[]>> {
  return useQuery({
    queryKey: RECONCILIATION_FLAGS_KEY,
    queryFn: async () => (await api.listReconciliationFlags()).data,
    enabled,
  });
}

function useInvalidateReconciliation(): () => Promise<void> {
  const qc = useQueryClient();
  return async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: RECONCILIATION_METRICS_KEY }),
      qc.invalidateQueries({ queryKey: RECONCILIATION_FLAGS_KEY }),
    ]);
  };
}

export function useRunReconciliation(): ReturnType<
  typeof useMutation<ReconciliationRunResultWire, Error, void>
> {
  const invalidate = useInvalidateReconciliation();
  return useMutation({
    mutationFn: () => api.runReconciliation(),
    onSuccess: () => {
      void invalidate();
    },
  });
}

export function useResolveReconciliationFlag(): ReturnType<
  typeof useMutation<
    ReconciliationFlagWire,
    Error,
    { id: string; input: ResolveReconciliationFlagInput }
  >
> {
  const invalidate = useInvalidateReconciliation();
  return useMutation({
    mutationFn: async ({ id, input }) => (await api.resolveReconciliationFlag(id, input)).data,
    onSuccess: () => {
      void invalidate();
    },
  });
}
